import { useAuthStep } from "@/hooks/useAuthStep";
import { useEffect, useRef, useState } from "react";
import { createClient } from '@supabase/supabase-js';


import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel"
import { Switch } from "@/components/ui/switch"

import Color from 'color';


import { Monitor, Moon, Palette, Star, Sun, TrashIcon } from "lucide-react";
import { CLOUDFLARE_URL, SUPABASE_ANON_KEY, SUPABASE_URL } from "@/lib/config";
import { trackEvent } from "@/lib/utils";
import { useCommunication } from "@/lib/CommunicationWrapper";

const TrendingTheme = ({ theme, setDarkMode, setSelectedTheme, clearTheme, selectedTheme }: { theme: any, setDarkMode: (darkMode: string) => void, setSelectedTheme: (theme: any) => void, clearTheme: () => Promise<void>, selectedTheme: any }) => {

    const { updateOptions, useRerenderListener, sendMessage, getOptions } = useCommunication();
    const selected = selectedTheme?.code === theme.code;

    const onClick = async () => {
        if (selected) {
            await clearTheme();
            return;
        }

        const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
        const { data, error } = await supabase.from("themes_public").select("title, code, exports").eq("code", theme.code).single();

        const exports = JSON.parse(data?.exports || "{}");

        // Determine if the theme is dark or light based on background-0 color
        let isDarkMode = false;
        if (exports?.dark_preset && exports?.dark_preset['background-0']) {
            try {
                const backgroundColor = Color(exports.dark_preset['background-0']);
                const luminosity = backgroundColor.luminosity();
                isDarkMode = luminosity < 0.5; // If luminosity < 0.5, it's considered dark
            } catch (error) {
                console.error("Error parsing color:", error);
                // Fallback: assume light mode if color parsing fails
                isDarkMode = false;
            }
        }

        setDarkMode(isDarkMode ? "dark" : "light");
        setSelectedTheme(data);

        updateOptions({
            "selected_theme": {
                "title": data?.title,
                "code": data?.code,
                "category": null,
                "exports": exports,
                "needs_reapply": (getOptions()?.card_ids || []).length === 0
            },
        });

        sendMessage({
            type: "LOAD_THEME",
            payload: { "exports": exports },
        });
    }

    return (
        <CarouselItem className="basis-1/5 flex flex-col cursor-pointer" onClick={onClick}>
            <div className={`h-full p-0.5 rounded-lg overflow-hidden ${selected ? "bg-brand-gradient" : "bg-none"}`}>
                <div className={`h-full w-full bg-black rounded-lg overflow-hidden relative`}>
                    <AppliedThemeTag selected={selected} />
                    <img src={theme.preview} className="w-full h-full object-cover" />
                    <div className="text-white p-[10px] bg-black/20 absolute bottom-0 left-0 right-0 h-full flex flex-col justify-between">
                        <p className="text-sm font-semibold">{theme.title}</p>
                        <p className="text-xs font-medium">{"@" + theme.credits}</p>
                    </div>
                </div>
            </div>
        </CarouselItem>
    )
}

const AppliedThemeTag = ({ selected }: { selected: boolean }) => {

    if (!selected) return null;

    return (
        <div className="z-10 absolute right-1.5 top-1.5 bg-black text-white px-1.5 py-0.5 rounded-full text-[10px] font-medium shadow-lg flex items-center gap-1">
            <Star className="w-2.5 h-2.5 fill-current" />
            <span>Applied</span>
        </div>
    )
}

const QuickSettings = () => {

    const init = useRef(false);
    const [trendingThemes, setTrendingThemes] = useState([]);
    const [selectedTheme, setSelectedTheme] = useState(null);

    const { updateOptions, useRerenderListener } = useCommunication();

    const [darkMode, setDarkMode] = useState("dark");

    const getTrendingThemes = async () => {
        const response = await fetch(`${CLOUDFLARE_URL}/themes/trending`);
        const { message, error } = await response.json();
        if (error) {
            console.error("Error fetching trending themes:", error);
            return;
        }
        setTrendingThemes(message);
    }

    useEffect(() => {
        if (!init.current) {
            getTrendingThemes();
            init.current = true;
        }
    }, []);


    const changes = useRef({});
    const { setAuthStep, uuid } = useAuthStep();

    const saveChanges = () => {
        setAuthStep("logins");
    }

    const renderTrendingThemes = () => {
        return trendingThemes.map((theme: any) => {
            return <TrendingTheme key={theme.code} theme={theme} setDarkMode={setDarkMode} setSelectedTheme={setSelectedTheme} clearTheme={clearTheme} selectedTheme={selectedTheme} />
        })
    }

    const clearTheme = async () => {
        setSelectedTheme(null);
        updateOptions({ "selected_theme": { "title": null, "code": null, "category": null } });
        return;
    }

    const { options, trigger } = useRerenderListener(["dark_mode", "sidebar", "todo", "search"]);

    useEffect(() => {
        trackEvent("signup_settings_loaded", { "distinct_id": uuid });
    }, []);


    return (
        <div className="px-[30px] flex flex-col gap-2 h-full mb-[30px]">
            <div className="w-full flex flex-col">
                <div className="flex items-center gap-[10px]">
                    <div>
                        <h1 className="text-2xl font-semibold">Choose your vibe</h1>
                        <p className="text-sm text-gray-500 mt-[5px]">You can change these anytime in the preference menu</p>
                    </div>
                </div>
                <div className="mt-2">
                    <Carousel className="mt-[10px] w-full relative">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center ml-[1px] mb-[5px]">
                                <h2 className="text font-medium">Trending themes</h2>
                                <div className={`${selectedTheme ? "cursor-pointer" : "cursor-default"} ml-5 flex gap-1 items-center ${selectedTheme ? "text-gray-800" : "text-gray-400"} transition-all`}
                                    onClick={clearTheme}
                                >
                                    <TrashIcon className="w-[13px] h-[13px]" />
                                    <p className="text-sm">Clear</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-[10px]">
                                <CarouselPrevious className="static bg-white" />
                                <CarouselNext className="static bg-white" />
                            </div>
                        </div>
                        <CarouselContent className="h-[100px] w-full">
                            {renderTrendingThemes()}
                        </CarouselContent>

                    </Carousel>
                </div>
                <div className="mt-[20px]">
                    <h2 className="font-medium">Color mode</h2>
                    <p className="text-md text-gray-500 text-sm">Choose if Canvas appearance should be light or dark, or follow your device appearance.</p>
                    <div className="flex items-center gap-[10px] font-medium mt-[10px]">
                        <button className={`bg-white rounded-full px-[15px] py-[6px] border border-gray-300 flex items-center gap-[10px] ${options.dark_mode === false ? "!bg-blue-100 !border-blue-200" : ""}`} onClick={() => updateOptions({ "dark_mode": false })}><Sun className="w-[16px] h-[16px]" /> Light</button>
                        <button className={`bg-white rounded-full px-[15px] py-[6px] border border-gray-300 flex items-center gap-[10px] ${options.dark_mode === true ? "!bg-blue-100 !border-blue-200" : ""}`} onClick={() => updateOptions({ "dark_mode": true })}><Moon className="w-[16px] h-[16px]" /> Dark</button>
                        <button className={`bg-white rounded-full px-[15px] py-[6px] border border-gray-300 flex items-center gap-[10px] ${options.dark_mode === "device" ? "!bg-blue-100 !border-blue-200" : ""}`} onClick={() => updateOptions({ "dark_mode": "device" })}><Monitor className="w-[16px] h-[16px]" />Device</button>
                    </div>
                </div>
                <div className="mt-[20px]">
                    <h2 className="font-medium">BetterCampus features</h2>
                    <p className="text-md text-gray-500 text-sm">Some of our most beloved features</p>
                    <div className="flex items-center gap-[30px] mt-[20px]">
                        <div className="flex gap-[10px] items-center">
                            <Switch variant="default" height={18} checked={options.clean_sidebar === true} onCheckedChange={(checked: boolean) => updateOptions({ "clean_sidebar": checked ? true : false })} />
                            <div>
                                <p className="font-medium">Sidebar</p>
                                <p className="text-sm text-gray-500">Cleaner, smoother</p>
                            </div>
                        </div>
                        <div className="flex gap-[10px] items-center">
                            <Switch variant="default" height={18} checked={options.better_todo === true} onCheckedChange={(checked: boolean) => updateOptions({ "better_todo": checked ? true : false })} />
                            <div>
                                <p className="font-medium">To-Do list</p>
                                <p className="text-sm text-gray-500">Progress indicator + confetti</p>
                            </div>
                        </div>
                        <div className="flex gap-[10px] items-center">
                            <Switch variant="default" height={18} checked={options.global_search === true} onCheckedChange={(checked: boolean) => updateOptions({ "global_search": checked ? true : false })} />
                            <div>
                                <p className="font-medium">Search Bar</p>
                                <p className="text-sm text-gray-500">Find anything in seconds</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex mt-auto gap-2 justify-end">
                <button onClick={saveChanges} className="bg-blue-500 text-white px-[20px] py-[8px] rounded-full" style={{ "background": "linear-gradient(120deg, var(--brand-1), var(--brand-2))" }}>Continue</button>
            </div>
        </div>
    )
}

export default QuickSettings;